<script lang="ts">
  // TODO(GCR): Add automatic SEO via https://schema.org/VideoObject
  import { browser } from '$app/env';
  import { onDestroy, onMount } from 'svelte';
  import VideoJs from 'video.js';
  import { createEventDispatcher } from 'svelte';
  import 'video.js/dist/video-js.min.css';
  import './video-js.postcss';

  export let videoSources: string[];
  export let videoName: string;
  export let autoplay: boolean;

  const fallbackVideosBaseUrl = import.meta.env.VITE_CDN_VIDEO_URL;
  const fallbackCaptionsBaseUrl = import.meta.env.VITE_CDN_CAPTION_URL;

  interface $$Events extends Omit<Partial<HTMLButtonElement>, 'click'> {
    video404: string;
    videoEnded: null;
  }
  const dispatch = createEventDispatcher<{
    video404: string;
    videoEnded: null;
  }>();

  interface ReloadSourceOnErrorArgument {
    getSource?: (reload: Function) => void;
    errorInterval?: number;
  }

  // Merge reloadSourceOnError
  interface PlayerWithReload extends VideoJs.Player {
    reloadSourceOnError: (reloadArg?: ReloadSourceOnErrorArgument) => void;
  }

  $: sources = videoSources.map((src) => ({
    src,
    type: src.includes('.m3u8') ? 'application/x-mpegURL' : 'video/mp4',
  }));

  $: {
    createPlayer(sources);
  }

  let player: PlayerWithReload;
  let videoDiv: HTMLDivElement;

  onMount(() => {
    if (!browser) return;
    createPlayer(sources);
  });

  function createPlayer(sources: { src: string; type: string }[]) {
    if (!videoDiv || !browser) return;
    if (player) player.dispose();
    videoDiv.innerHTML = '';
    const video = document.createElement('video');
    video.setAttribute('crossorigin', 'anonymous');
    video.classList.add('video-js', 'vjs-16-9', 'vjs-big-play-centered');
    videoDiv.append(video);

    let hasFallenBack = false;

    player = VideoJs(
      video,
      {
        controls: true,
        autoplay,
        preload: 'auto',
        playbackRates: [0.5, 0.75, 1, 1.25, 1.5, 2],
        sources,
      },
      // The player on-ready function
      playerReady
    ) as PlayerWithReload;

    function playerReady() {
      const userSettings = get();
      player.playbackRate(userSettings.playbackRate);
      if (userSettings.captions) {
        const tracks = Array.from(player.textTracks());
        tracks.find((track) => {
          if (track.language === 'en') {
            track.mode = 'showing';
            return true;
          }
          return false;
        });
      }

      const fallbackSource = {
        src: `${fallbackVideosBaseUrl}/lessons/${videoName}Medium.mp4`,
        type: 'video/mp4',
      };

      function videoErrorHandler() {
        // We might yet solve this!
        if (!hasFallenBack) return;
        const error = player.error() || { code: 0, message: 'An unknown video error occurred.' };
        console.error(error);
        // if (error?.code > 2 || error?.code === 0) {
        //   Rollbar.error(error);
        // }
        const errorMessage =
          error.code !== 4
            ? error.message
            : `The video could not be loaded, either because the network failed or because
          your browser blocked the download of ${fallbackSource.src}`;
        console.error(errorMessage);
        dispatch('video404', errorMessage);
      }
      player.on('error', videoErrorHandler);

      player.reloadSourceOnError({
        // getSource allows you to override the source object used when an error occurs
        getSource(reload) {
          if (hasFallenBack) {
            return;
          } // No more fallbacks...
          console.error('Loading original sources failed, trying fallback video sources.');
          hasFallenBack = true;

          // call reload() with a fresh source object
          // you can do this step asynchronously if you want (but the error dialog will
          // show up while you're waiting)
          reload(fallbackSource);
          player.addRemoteTextTrack(
            {
              src: `${fallbackCaptionsBaseUrl}/${videoName}Medium.vtt`,
              kind: 'captions',
              srclang: 'en',
              default: true,
            },
            false
          );
        },

        // errorInterval specifies the minimum amount of seconds that must pass before
        // another reload will be attempted
        errorInterval: 5,
      });

      player.volume(userSettings.volume);
      player.on('volumechange', () => set({ volume: player.volume() }));
      video.addEventListener('ratechange', () => set({ playbackRate: player.playbackRate() }));

      player.on('timeupdate', () => {
        const tracks = Array.from(player.textTracks());
        if (get().captions && player.currentTime() < 1) {
          tracks.find((track) => {
            if (track.language === 'en') {
              track.mode = 'showing';
              return true;
            }
            return false;
          });
          return;
        }
        const englishTracks = Array.from(player.textTracks()).filter(
          (track) => track.language === 'en'
        );
        const isShowing =
          englishTracks.length && englishTracks.find((track) => track.mode === 'showing');
        if (get().captions !== !!isShowing) set({ captions: !!isShowing });
      });

      if (userSettings.fullscreen) {
        player.requestFullscreen();
      }

      player.on('fullscreenchange', () => {
        if (player.currentTime() < 1) return;
        set({ fullscreen: player.isFullscreen() });
      });

      player.on('ended', () => {
        dispatch('videoEnded', null);
      });
    }
  }

  onDestroy(() => {
    if (player) player.dispose();
  });

  interface VideoJSUserSettings {
    volume: number;
    captions: boolean;
    playbackRate: number;
    fullscreen: boolean;
  }

  let userSettings: VideoJSUserSettings;

  function set(settings: Partial<VideoJSUserSettings>) {
    userSettings = {
      ...userSettings,
      ...settings,
    };
    try {
      window.localStorage.setItem('uTheoryVJSUserSettings', JSON.stringify(userSettings));
    } catch (err) {
      // Eat the error.
    }
  }

  function get() {
    userSettings = userSettings || {
      volume: 1,
      captions: false,
      playbackRate: 1,
      fullscreen: false,
    };
    try {
      const json = window.localStorage.getItem('uTheoryVJSUserSettings');
      userSettings = {
        ...userSettings,
        ...JSON.parse(json!),
      };
    } catch (err) {
      // Eat it.
    }
    return userSettings;
  }
</script>

<div bind:this={videoDiv} />
